import { useStore } from '@tanstack/react-form'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ChevronLeftIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useAppForm } from '@hooks/form'
import useErrorMessage from '@hooks/use-error-message'

import { Button } from '@shadcn/button'

import { useMutationCompanyPatch } from '@api/company/company.mutations'
import { queryCompanyGet } from '@api/company/company.queries'

import { dateToIsoString, zodDateString } from '@/lib/zod'

const params = z.object({
  uuid: z.uuid(),
})

export const Route = createFileRoute('/app/data/companies/$uuid/edit')({
  component: RouteComponent,
  params,
})

const schema = z
  .object({
    mainCurrencyCode: z.string().nullable(),
    balanceSheetAmountCents: z.coerce.number<number>().nullable(),
    balanceSheetDate: zodDateString.nullable(),
    numberOfClients: z.coerce.number<number>().nullable(),
    numberOfClientsDate: zodDateString.nullable(),
    users: z.array(z.uuid()),
  })
  .partial()

type Schema = z.infer<typeof schema>

function RouteComponent() {
  const { t } = useTranslation()
  const { uuid } = Route.useParams()
  const { data } = useSuspenseQuery(queryCompanyGet(uuid))
  const { mutate, isPending } = useMutationCompanyPatch()
  const errorMessage = useErrorMessage()
  const router = useRouter()

  const defaultValues: Schema = {
    mainCurrencyCode: data.commonFields.mainCurrencyCode,
    balanceSheetAmountCents: data.commonFields.balanceSheetAmountCents
      ? data.commonFields.balanceSheetAmountCents / 100
      : null,
    balanceSheetDate: data.commonFields.balanceSheetDate ?? null,
    numberOfClients: data.commonFields.numberOfClients,
    numberOfClientsDate: data.commonFields.numberOfClientsDate ?? null,
    users: data.users.map((user) => user.uuid),
  }

  const form = useAppForm({
    validators: { onSubmit: schema },
    defaultValues,
    onSubmit: ({ value }) => {
      mutate(
        {
          uuid,
          request: {
            ...value,
            // Convert whole currency to cents
            balanceSheetAmountCents: value.balanceSheetAmountCents
              ? value.balanceSheetAmountCents * 100
              : null,
            balanceSheetDate: value.balanceSheetDate?.trim()
              ? dateToIsoString(value.balanceSheetDate)
              : null,
            numberOfClientsDate: value.numberOfClientsDate?.trim()
              ? dateToIsoString(value.numberOfClientsDate)
              : null,
          },
        },
        {
          onSuccess: () => {
            toast.success(t('data.companies.uuid.edit.success'))
            router.history.back()
          },
          onError: (error) => {
            const message = errorMessage({ error })
            toast.error(message)
          },
        },
      )
    },
  })

  const mainCurrencyCode = useStore(
    form.store,
    (state) => state.values.mainCurrencyCode,
  )

  const initialUserLabels = Object.fromEntries(
    data.users.map((user) => [
      user.uuid,
      `${user.firstName} ${user.lastName} (${user.email})`,
    ]),
  )

  return (
    <form
      className="flex h-full w-full flex-col divide-y overflow-y-auto"
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outlineGray"
            className="size-7 rounded-sm"
            size="ghost"
            type="button"
            onClick={() => router.history.back()}
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <h1 className="text-text-primary-900 text-lg font-bold">
            {t('data.companies.uuid.edit.title')}
          </h1>
        </div>

        <form.AppForm>
          <form.SubmitButton pending={isPending} variant="primary" size="xxs">
            {t('data.companies.uuid.edit.submit')}
          </form.SubmitButton>
        </form.AppForm>
      </div>
      <div className="flex grow justify-center py-6">
        <div className="flex max-w-184 grow flex-col gap-6">
          <form.AppField name="mainCurrencyCode">
            {(field) => (
              <field.CurrencySelectField
                label={t('data.companies.uuid.edit.fields.main-currency.label')}
                className="w-full"
              />
            )}
          </form.AppField>
          <div className="flex w-full items-end gap-2">
            <div className="w-1/2">
              <form.AppField name="balanceSheetAmountCents">
                {(field) => (
                  <field.NumberInputField
                    currency={mainCurrencyCode}
                    label={t(
                      'data.companies.uuid.edit.fields.balance-sheet-amount.label',
                    )}
                    placeholder={t(
                      'data.companies.uuid.edit.fields.balance-sheet-amount.placeholder',
                    )}
                  />
                )}
              </form.AppField>
            </div>
            <div className="w-1/2">
              <form.AppField name="balanceSheetDate">
                {(field) => <field.TextField type="date" />}
              </form.AppField>
            </div>
          </div>
          <div className="flex w-full items-end gap-2">
            <div className="w-1/2">
              <form.AppField name="numberOfClients">
                {(field) => (
                  <field.NumberInputField
                    label={t(
                      'data.companies.uuid.edit.fields.number-of-clients.label',
                    )}
                    placeholder={t(
                      'data.companies.uuid.edit.fields.number-of-clients.placeholder',
                    )}
                  />
                )}
              </form.AppField>
            </div>
            <div className="w-1/2">
              <form.AppField name="numberOfClientsDate">
                {(field) => <field.TextField type="date" />}
              </form.AppField>
            </div>
          </div>
          <form.AppField name="users">
            {(field) => (
              <field.UserMultiSelectField
                additionalLabels={initialUserLabels}
              />
            )}
          </form.AppField>
        </div>
      </div>
    </form>
  )
}
